import { useEffect, useState } from "react";

/**
 * usePlatformStats — fetches DB-derived feature/template/package counts
 * from /api/public/platform-stats so marketing surfaces never hardcode
 * "150 features" values that go stale when features are added/removed.
 *
 * Returns `{stats, loaded}` — while loading, `stats` holds the passed
 * defaults so the UI never paints "undefined".
 *
 * W2-UIUX-NEW-003 — persist to sessionStorage so returning visitors
 * don't see the stale-default flash while the fetch resolves.
 */
const DEFAULTS = {
    total_features: 150,
    total_templates: 172,
    total_packages: 5,
    per_package_feature_counts: {},
};

const SS_KEY = "ddip_platform_stats_v1";

function _readSession() {
    try {
        const raw = sessionStorage.getItem(SS_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function _writeSession(obj) {
    try { sessionStorage.setItem(SS_KEY, JSON.stringify(obj)); } catch { /* ignore */ }
}

let _cache = _readSession();

export default function usePlatformStats() {
    const [stats, setStats] = useState(_cache || DEFAULTS);
    const [loaded, setLoaded] = useState(!!_cache);

    useEffect(() => {
        if (_cache) return;
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
        fetch(`${base}/api/public/platform-stats`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (d) {
                    _cache = d;
                    _writeSession(d);
                    setStats(d);
                }
            })
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, []);

    return { stats, loaded };
}
